import { useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";
import { Button } from "../../components/ui/button";

const HELP_CONTENT = {
  '/admin': {
    title: 'Dashboard',
    sections: [
      { heading: 'Overview', text: 'The dashboard shows today\'s revenue, transaction count, average order value, and any failed PMS postings. Data refreshes every 30 seconds.' },
      { heading: 'Recent Transactions', text: 'Lists the last 20 transactions with room, operator, amount, and PMS status.' },
      { heading: 'Active Sessions', text: 'Shows all logged-in operators with device info. Use Force Logout to terminate a session.' },
      { heading: 'Failed Postings', text: 'If charges failed to post to the PMS, click "Retry All" to attempt re-posting.' },
    ]
  },
  '/admin/menu': {
    title: 'Menu Management',
    sections: [
      { heading: 'Categories', text: 'Each category has a name, display order, and Itemizer ID that controls how charges are split in the PMS (1=Food, 2=Beverages, 3=Merchandise).' },
      { heading: 'Items', text: 'Add menu items with a name, price, category, and tax rates. Item codes are auto-generated from the first 3 letters of the name plus a sequential number.' },
      { heading: 'Tax Rates', text: 'Assign multiple tax rates to each item using the checkboxes in the item form.' },
      { heading: 'Active/Inactive', text: 'Toggle items on/off using the switch. Inactive items won\'t appear in the POS.' },
    ]
  },
  '/admin/users': {
    title: 'User Management',
    sections: [
      { heading: 'Admin Users', text: 'Admins log in with username + password. Add an email address so they can use "Forgot Password" to reset via email.' },
      { heading: 'POS Operators', text: 'Operators log in with a 4-8 digit PIN on the POS terminal. PINs must be unique across all active operators.' },
      { heading: 'Actions', text: 'Reset PIN — set a new PIN for an operator. Unlock — clear a lockout from failed PIN attempts. Force Logout — terminate all active sessions.' },
      { heading: 'Cashier ID', text: 'Each user gets an auto-assigned numeric cashier ID used for PMS charge postings.' },
    ]
  },
  '/admin/taxes': {
    title: 'Tax Rates',
    sections: [
      { heading: 'Tax Configuration', text: 'Create tax rates with a name, percentage, and inclusive/exclusive setting. Assign taxes to menu items in Menu Management.' },
      { heading: 'Inclusive vs Exclusive', text: 'Inclusive: tax is embedded in the item price. Exclusive: tax is added on top of the price.' },
      { heading: 'Multiple Taxes', text: 'Items can have multiple tax rates (e.g., Tax 1 at 7% + Tax 2 at 10%). Each is calculated and displayed separately.' },
    ]
  },
  '/admin/transactions': {
    title: 'Transactions',
    sections: [
      { heading: 'Filtering', text: 'Filter by status, room number, date range, or operator. Click a row to expand and see line items, taxes, and device info.' },
      { heading: 'Status Badges', text: 'Posted (green) = sent to PMS. Local Only (amber) = saved locally, PMS posting failed. Voided (gray) = reversed. Failed (red) = needs retry.' },
      { heading: 'Void', text: 'Click Void on a posted transaction to reverse it. A negative charge is sent to the PMS. You must enter a reason.' },
      { heading: 'Retry', text: 'Click Retry on failed or local-only transactions to re-attempt PMS posting.' },
    ]
  },
  '/admin/reports': {
    title: 'Reports',
    sections: [
      { heading: 'Report Types', text: 'Room Sales — by room with line items. Article Sales — by category with item totals. Gratuity — by cashier. Unposted — failed PMS charges. Plus daily sales, shift, tax, and void reports.' },
      { heading: 'Quick Dates', text: 'Use Today, Yesterday, 7 Days, 30 Days, or Last Month buttons to quickly set the date range.' },
      { heading: 'Export', text: 'CSV — downloads the data as a spreadsheet. Print — prints just the report (not the navigation). Email — sends the report as a PDF attachment.' },
    ]
  },
  '/admin/audit': {
    title: 'Audit Log',
    sections: [
      { heading: 'Immutable Log', text: 'Every action (login, charge, void, setting change, user creation) is recorded with timestamp, user, device info, and IP address.' },
      { heading: 'Expanding Rows', text: 'Click a row to see before/after values for changes, and the SHA-256 record hash for tamper verification.' },
      { heading: 'Filtering', text: 'Filter by action type: Login, Charge, Void, Create, Update, Settings.' },
    ]
  },
  '/admin/settings': {
    title: 'Settings',
    sections: [
      { heading: 'Session Timeout', text: 'How many minutes of inactivity before a POS operator is automatically logged out.' },
      { heading: 'Max PIN Attempts', text: 'How many wrong PIN entries before an operator account is locked for 30 minutes.' },
      { heading: 'Auto Gratuity', text: 'Percentage automatically added to each charge. Operators can remove it per transaction. Set to 0 to disable.' },
      { heading: 'Property', text: 'Hotel name, property code, currency, and revenue center used in PMS postings.' },
      { heading: 'IP Access Whitelist', text: 'Restrict access to the application by IP address. Add IP addresses or CIDR ranges (e.g. 192.168.1.0/24). Only whitelisted IPs can access any page. Removing all entries disables the restriction. WARNING: removing your own IP will lock you out.' },
    ]
  },
};

export default function HelpPanel() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Match current path to help content
  const path = location.pathname;
  const help = HELP_CONTENT[path] || HELP_CONTENT[Object.keys(HELP_CONTENT).find(k => path.startsWith(k))] || null;

  if (!help) return null;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 z-50 w-10 h-10 bg-black text-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-800 transition-colors print:hidden"
        data-testid="help-button"
        title="Help"
      >
        {open ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed bottom-16 right-4 z-50 w-80 max-h-[70vh] bg-white border border-gray-200 shadow-xl overflow-y-auto print:hidden animate-fade-in"
             data-testid="help-panel">
          <div className="sticky top-0 bg-black text-white px-4 py-3">
            <h3 className="font-heading text-sm font-bold tracking-tight">{help.title}</h3>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Help</p>
          </div>
          <div className="p-4 space-y-4">
            {help.sections.map((s) => (
              <div key={s.heading}>
                <h4 className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-1">{s.heading}</h4>
                <p className="text-xs text-gray-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
